import { Controller } from '@hotwired/stimulus'
import { debounce } from 'lodash'
import { resolveCarrierWithErrorReporting } from '@utils/carriers'

export default class extends Controller {
  static targets = ['trackingNumberInput', 'carrierInput', 'notificationMessage']

  async connect() {
    const shouldResolveCarrierOnConnect =
      this.hasTrackingNumberInputTarget &&
      this.hasCarrierInputTarget &&
      this.trackingNumberInputTarget.value && this.trackingNumberInputTarget.value.length > 0 &&
      !this.carrierInputTarget.value

    if (shouldResolveCarrierOnConnect) {
      this.carrierResolvedOnConnect = true

      try {
        // Call the handler directly with a synthetic event
        const syntheticEvent = { target: this.trackingNumberInputTarget }
        await this.handleTrackingNumberChange(syntheticEvent, { reRaiseErrors: true })
      } catch (error) {
        console.error('Initial carrier resolution failed:', error.message)
      } finally {
        this.carrierResolvedOnConnect = false
      }
    } else {
      // If no resolution needed, immediately set to false
      this.carrierResolvedOnConnect = false
    }
  }

  trackingNumberInputTargetConnected(element) {
    element.addEventListener('keyup', this.debouncedTrackingNumberChangeHandler)
  }

  trackingNumberInputTargetDisconnected(element) {
    element.removeEventListener('keyup', this.debouncedTrackingNumberChangeHandler)
  }

  carrierInputTargetConnected(element) {
    element.addEventListener('change', () => this.hideNotificationMessage())
  }

  carrierInputTargetDisconnected(element) {
    element.removeEventListener('change', () => this.hideNotificationMessage())
  }

  get boundTrackingNumberChangeHandler() {
    return this.handleTrackingNumberChange.bind(this)
  }

  get debouncedTrackingNumberChangeHandler() {
    return debounce(this.boundTrackingNumberChangeHandler, 350, { trailing: true })
  }

  async handleTrackingNumberChange(event, { reRaiseErrors = false } = {}) {
    if (!this.carrierResolvedOnConnect) this.hideNotificationMessage()

    const trackingNumber = event.target.value

    if (!!trackingNumber && trackingNumber.length) {
      try {
        const resolvedCarrier = await resolveCarrierWithErrorReporting(trackingNumber)

        if (this.hasCarrierInputTarget) {
          this.carrierInputTarget.value = resolvedCarrier

          // Only show the notification message if the carrier was resolved during initial connect
          if (this.carrierResolvedOnConnect) this.showNotificationMessage()
        }

      } catch (e) {
        if (this.hasCarrierInputTarget) {
          this.carrierInputTarget.value = ''
          this.hideNotificationMessage()
        }

        // Re-raise the error only if explicitly requested
        if (reRaiseErrors) {
          throw e;
        } else {
          console.log('Carrier resolution failed:', e.message);
        }
      }
    } else {
      if (this.hasCarrierInputTarget) {
        this.carrierInputTarget.value = ''
        this.hideNotificationMessage()
      }
    }
  }

  showNotificationMessage() {
    if (this.hasNotificationMessageTarget) {
      this.notificationMessageTarget.classList.remove('d-none')
      this.notificationMessageTarget.classList.add('d-block')
      this.notificationMessageTarget.style.animation = 'fadeIn 0.5s'
    }
  }

  hideNotificationMessage() {
    if (this.hasNotificationMessageTarget) {
      this.notificationMessageTarget.classList.add('d-none')
      this.notificationMessageTarget.classList.remove('d-block')
    }
  }
}
